/**
 * Minimal Working Drag & Drop Editor
 * 
 * Simple implementation that actually works for positioning components
 */

import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, Divider, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import {
  Delete as DeleteIcon,
  DragHandle as DragHandleIcon,
  Layers as LayersIcon,
  Apps as AppsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import ComponentRenderer from './renderers/ComponentRenderer';
import { PropertyPanel } from './PropertyPanel';
import { LayoutPanel } from './LayoutPanel';
import { StylePanel } from './StylePanel';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
  useDraggable,
  DragOverEvent,
} from '@dnd-kit/core';

import { PageDoc, Section, Row, Column, Block, ComponentType, EditorState, Style, LayoutAction } from '../../types/page-builder.types';
import { ComponentRegistry, getDefaultProps } from '../../utils/component-registry';
import { convertStyleToSx } from '../../utils/style-converter';

interface MinimalDragDropEditorProps {
  pageDoc: PageDoc;
  onPageUpdate: (pageDoc: PageDoc) => void;
  onSave?: () => void;
  readonly?: boolean;
  teamId: string;
  organizationId?: string;
}

export const MinimalDragDropEditor: React.FC<MinimalDragDropEditorProps> = ({
  pageDoc,
  onPageUpdate,
  onSave,
  readonly = false,
  teamId,
  organizationId
}) => {
  const [editorState, setEditorState] = useState<EditorState>({
    selectedBlockId: undefined,
    selectedElementId: undefined,
    selectedElementType: undefined,
    isDragging: false,
    clipboard: undefined,
    history: {
      past: [],
      present: pageDoc,
      future: [],
      canUndo: false,
      canRedo: false
    }
  });

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overContainer, setOverContainer] = useState<string | null>(null);
  const [leftPanelTab, setLeftPanelTab] = useState<'components' | 'layout'>('components');
  const [layoutExpandedState, setLayoutExpandedState] = useState<{
    sections: Set<string>;
    rows: Set<string>;
  } | undefined>();
  const [showLeftPanel, setShowLeftPanel] = useState<boolean>(true);
  const [showRightPanel, setShowRightPanel] = useState<boolean>(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Sync pageDoc prop changes to internal state (for undo/redo from parent)
  React.useEffect(() => {
    setEditorState(prev => ({
      ...prev,
      history: {
        ...prev.history,
        present: pageDoc
      }
    }));
  }, [pageDoc]);

  // Keyboard shortcuts for toggling panels
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + B to toggle left panel
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setShowLeftPanel(prev => !prev);
      }
      // Cmd/Ctrl + I to toggle right panel (properties inspector)
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        setShowRightPanel(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const updatePage = useCallback((updatedPage: PageDoc) => {
    // Don't update local history - let parent component manage it
    onPageUpdate(updatedPage);
  }, [onPageUpdate]);

  const selectBlock = useCallback((blockId: string) => {
    setEditorState(prev => ({ 
      ...prev, 
      selectedBlockId: blockId,
      selectedElementId: undefined,
      selectedElementType: undefined 
    }));
    // Properties now shown automatically when block selected
  }, []);

  const selectElement = useCallback((elementId: string, elementType: 'section' | 'row' | 'column') => {
    setEditorState(prev => ({ 
      ...prev, 
      selectedBlockId: undefined,
      selectedElementId: elementId,
      selectedElementType: elementType 
    }));
    // Style now integrated with properties
  }, []);

  const deleteBlock = useCallback((blockId: string) => {
    if (readonly) return;

    // CRITICAL: Deep clone to prevent mutating the original pageDoc reference
    const updatedPage = JSON.parse(JSON.stringify(pageDoc));
    
    for (const section of updatedPage.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          const blockIndex = column.blocks.findIndex(b => b.id === blockId);
          if (blockIndex !== -1) {
            column.blocks.splice(blockIndex, 1);
            updatePage(updatedPage);
            return;
          }
        }
      }
    }
  }, [pageDoc, updatePage, readonly]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(String(active.id));
    setEditorState(prev => ({ ...prev, isDragging: true }));
    console.log('Drag start:', active.id, active.data.current);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverContainer(over ? String(over.id) : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setOverContainer(null);
    setEditorState(prev => ({ ...prev, isDragging: false }));

    console.log('Drag end:', {
      activeId: active.id,
      overId: over?.id,
      activeData: active.data.current,
      overData: over?.data.current
    });

    if (!over) {
      console.log('No drop target');
      return;
    }

    const dragType = active.data.current?.type;
    const dropType = over.data.current?.type;

    if (dragType === 'component') {
      // Adding new component from palette
      const componentType = active.data.current?.componentType;
      if (componentType) {
        addComponent(componentType, over.id, over.data.current);
      }
    } else if (dragType === 'block') {
      // Moving existing block
      moveBlock(active.data.current, over.id, over.data.current);
    }
  };

  const addComponent = (componentType: ComponentType, overId: any, overData: any) => {
    console.log('Adding component:', { componentType, overId, overData });

    if (readonly) return;

    const newBlock: Block = {
      id: `block-${Date.now()}`,
      type: componentType,
      props: getDefaultProps(componentType)
    };

    // CRITICAL: Deep clone to prevent mutating the original pageDoc reference
    const updatedPage = JSON.parse(JSON.stringify(pageDoc));
    
    // Ensure we have structure
    if (updatedPage.layout.length === 0) {
      updatedPage.layout.push(createDefaultSection());
    }

    // Handle different drop targets
    if (overData?.type === 'column') {
      const column = findColumn(updatedPage, overData.columnId);
      if (column) {
        const insertIndex = overData.index !== undefined ? overData.index : column.blocks.length;
        column.blocks.splice(insertIndex, 0, newBlock);
        console.log('Added to column', overData.columnId, 'at index', insertIndex);
      }
    } else if (overData?.type === 'empty') {
      // Empty page
      const firstSection = updatedPage.layout[0];
      if (firstSection.rows.length === 0) {
        firstSection.rows.push(createDefaultRow());
      }
      const firstColumn = firstSection.rows[0].columns[0];
      firstColumn.blocks.push(newBlock);
      console.log('Added to empty page');
    } else {
      // Try to find column by ID
      const column = findColumn(updatedPage, String(overId));
      if (column) {
        column.blocks.push(newBlock);
        console.log('Added to column by ID', overId);
      } else {
        // Default fallback
        const firstSection = updatedPage.layout[0];
        if (firstSection.rows.length === 0) {
          firstSection.rows.push(createDefaultRow());
        }
        const firstColumn = firstSection.rows[0].columns[0];
        firstColumn.blocks.push(newBlock);
        console.log('Added to default location');
      }
    }

    setEditorState(prev => ({ ...prev, selectedBlockId: newBlock.id }));
    updatePage(updatedPage);
  };

  const moveBlock = (dragData: any, overId: any, overData: any) => {
    if (readonly || !dragData.block) return;

    // CRITICAL: Deep clone to prevent mutating the original pageDoc reference
    const updatedPage = JSON.parse(JSON.stringify(pageDoc));
    const sourceColumn = findColumn(updatedPage, dragData.sourceColumnId);

    if (!sourceColumn) return;

    // Remove from source
    const blockIndex = sourceColumn.blocks.findIndex(b => b.id === dragData.block.id);
    if (blockIndex === -1) return;

    const [movedBlock] = sourceColumn.blocks.splice(blockIndex, 1);

    // Add to target
    if (overData?.type === 'column') {
      const targetColumn = findColumn(updatedPage, overData.columnId);
      if (targetColumn) {
        let insertIndex = overData.index !== undefined ? overData.index : targetColumn.blocks.length;

        // If moving within the same column and moving downward, adjust index
        // because we've already removed the block from above
        if (sourceColumn === targetColumn && blockIndex < insertIndex) {
          insertIndex = insertIndex - 1;
        }

        targetColumn.blocks.splice(insertIndex, 0, movedBlock);
      }
    } else {
      // Try to find column by ID
      const targetColumn = findColumn(updatedPage, String(overId));
      if (targetColumn) {
        targetColumn.blocks.push(movedBlock);
      } else {
        // Put it back in source if we can't find target
        sourceColumn.blocks.splice(blockIndex, 0, movedBlock);
        return;
      }
    }

    updatePage(updatedPage);
  };

  const findColumn = (page: PageDoc, columnId: string): Column | null => {
    for (const section of page.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          if (column.id === columnId) {
            return column;
          }
        }
      }
    }
    return null;
  };

  const createDefaultSection = (): Section => ({
    id: `section-${Date.now()}`,
    kind: 'container',
    rows: [createDefaultRow()],
    style: {
      margin: { top: 0, right: 0, bottom: 5, left: 0 },
      border: { width: 0, style: 'solid', color: '#e0e0e0' },
      borderRadius: 0,
    },
    visible: true
  });

  const createDefaultRow = (): Row => ({
    id: `row-${Date.now()}`,
    columns: [{
      id: `col-${Date.now()}`,
      width: 12,
      blocks: [],
      style: {
        margin: { top: 0, right: 5, bottom: 0, left: 0 },
        border: { width: 0, style: 'solid', color: '#e0e0e0' },
        borderRadius: 0,
        minHeight: 100
      },
      visible: true
    }],
    style: {
      margin: { top: 0, right: 0, bottom: 5, left: 0 },
      border: { width: 0, style: 'solid', color: '#e0e0e0' },
      borderRadius: 0,
    },
    visible: true
  });

  const findBlockById = (page: PageDoc, blockId: string): Block | null => {
    for (const section of page.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          const block = column.blocks.find(b => b.id === blockId);
          if (block) return block;
        }
      }
    }
    return null;
  };

  const updateBlockInPage = (page: PageDoc, updatedBlock: Block): PageDoc => {
    // CRITICAL: Deep clone to prevent mutating the original pageDoc reference
    const newPage = JSON.parse(JSON.stringify(page));
    newPage.updatedAt = new Date().toISOString();

    for (const section of newPage.layout) {
      for (const row of section.rows) {
        for (const column of row.columns) {
          const blockIndex = column.blocks.findIndex(b => b.id === updatedBlock.id);
          if (blockIndex !== -1) {
            column.blocks[blockIndex] = updatedBlock;
            return newPage;
          }
        }
      }
    }

    return newPage;
  };

  // Layout manipulation functions
  const handleLayoutAction = useCallback((action: LayoutAction) => {
    if (readonly) return;

    // CRITICAL: Deep clone to prevent mutating the original pageDoc reference
    const updatedPage = JSON.parse(JSON.stringify(pageDoc));

    switch (action.type) {
      case 'ADD_SECTION': {
        const newSection = createDefaultSection();
        const position = action.position ?? updatedPage.layout.length;
        updatedPage.layout.splice(position, 0, newSection);
        break;
      }

      case 'ADD_ROW': {
        const section = updatedPage.layout.find(s => s.id === action.sectionId);
        if (section) {
          const newRow = createDefaultRow();
          const position = action.position ?? section.rows.length;
          section.rows.splice(position, 0, newRow);
        }
        break;
      }

      case 'ADD_COLUMN': {
        const section = updatedPage.layout.find(s => s.id === action.sectionId);
        const row = section?.rows.find(r => r.id === action.rowId);
        if (row) {
          // Calculate total allocated width in the row (max 12 units in Material UI grid)
          const MAX_WIDTH = 12;
          const currentTotalWidth = row.columns.reduce((sum, col) => sum + (col.width || 12), 0);
          const availableWidth = MAX_WIDTH - currentTotalWidth;

          // Determine width for new column
          let newColumnWidth: number;
          if (availableWidth > 0) {
            // Use available width, but no less than 1 unit
            newColumnWidth = availableWidth;
          } else {
            // No space available - shrink the widest column by 1 and allocate to new column
            const widestColumn = row.columns.reduce((max, col) =>
              (col.width || 12) > (max.width || 12) ? col : max
            , row.columns[0]);

            if (widestColumn && (widestColumn.width || 12) > 1) {
              widestColumn.width = (widestColumn.width || 12) - 1;
              newColumnWidth = 1;
            } else {
              // All columns are at minimum width, can't add more
              console.warn('Cannot add column: all columns at minimum width');
              break;
            }
          }

          const newColumn: Column = {
            id: `col-${Date.now()}`,
            width: newColumnWidth,
            blocks: [],
            style: {
              margin: { top: 0, right: 5, bottom: 0, left: 0 },
              border: { width: 0, style: 'solid', color: '#e0e0e0' },
              borderRadius: 0,
              minHeight: 100
            },
            visible: true
          };
          const position = action.position ?? row.columns.length;
          row.columns.splice(position, 0, newColumn);
        }
        break;
      }

      case 'DELETE_ELEMENT': {
        if (action.elementType === 'section') {
          const sectionIndex = updatedPage.layout.findIndex(s => s.id === action.elementId);
          if (sectionIndex !== -1) {
            updatedPage.layout.splice(sectionIndex, 1);
          }
        } else if (action.elementType === 'row') {
          for (const section of updatedPage.layout) {
            const rowIndex = section.rows.findIndex(r => r.id === action.elementId);
            if (rowIndex !== -1) {
              section.rows.splice(rowIndex, 1);
              break;
            }
          }
        } else if (action.elementType === 'column') {
          for (const section of updatedPage.layout) {
            for (const row of section.rows) {
              const columnIndex = row.columns.findIndex(c => c.id === action.elementId);
              if (columnIndex !== -1) {
                row.columns.splice(columnIndex, 1);
                break;
              }
            }
          }
        }
        break;
      }

      case 'TOGGLE_VISIBILITY': {
        if (action.elementType === 'section') {
          const section = updatedPage.layout.find(s => s.id === action.elementId);
          if (section) {
            section.visible = !section.visible;
          }
        } else if (action.elementType === 'row') {
          for (const section of updatedPage.layout) {
            const row = section.rows.find(r => r.id === action.elementId);
            if (row) {
              row.visible = !row.visible;
              break;
            }
          }
        } else if (action.elementType === 'column') {
          for (const section of updatedPage.layout) {
            for (const row of section.rows) {
              const column = row.columns.find(c => c.id === action.elementId);
              if (column) {
                column.visible = !column.visible;
                break;
              }
            }
          }
        }
        break;
      }

      case 'UPDATE_STYLE': {
        if (action.elementType === 'view') {
          if (action.elementId === updatedPage._id) {
            updatedPage.style = action.style;
          }
        } else if (action.elementType === 'section') {
          const section = updatedPage.layout.find(s => s.id === action.elementId);
          if (section) {
            section.style = action.style;
          }
        } else if (action.elementType === 'row') {
          for (const section of updatedPage.layout) {
            const row = section.rows.find(r => r.id === action.elementId);
            if (row) {
              row.style = action.style;
              break;
            }
          }
        } else if (action.elementType === 'column') {
          for (const section of updatedPage.layout) {
            for (const row of section.rows) {
              const column = row.columns.find(c => c.id === action.elementId);
              if (column) {
                column.style = action.style;
                break;
              }
            }
          }
        }
        break;
      }

      case 'UPDATE_COLUMN_WIDTH': {
        const MAX_WIDTH = 12;
        for (const section of updatedPage.layout) {
          for (const row of section.rows) {
            const columnIndex = row.columns.findIndex(c => c.id === action.columnId);
            if (columnIndex !== -1) {
              const column = row.columns[columnIndex];
              const requestedWidth = Math.max(1, Math.min(12, action.width)); // Clamp between 1-12

              // Calculate total width of OTHER columns in the row
              const otherColumnsWidth = row.columns.reduce((sum, col, idx) => {
                return idx === columnIndex ? sum : sum + (col.width || 12);
              }, 0);

              // Calculate maximum allowed width for this column
              const maxAllowedWidth = MAX_WIDTH - otherColumnsWidth;

              // Constrain the requested width to not exceed the row total
              const constrainedWidth = Math.min(requestedWidth, Math.max(1, maxAllowedWidth));

              column.width = constrainedWidth;

              // If we had to constrain the width, log a warning
              if (constrainedWidth < requestedWidth) {
                console.warn(
                  `Column width constrained from ${requestedWidth} to ${constrainedWidth}. ` +
                  `Total row width cannot exceed ${MAX_WIDTH} units. ` +
                  `Other columns occupy ${otherColumnsWidth} units.`
                );
              }
              break;
            }
          }
        }
        break;
      }

      case 'MOVE_ELEMENT': {
        if (action.elementType === 'section') {
          const currentIndex = updatedPage.layout.findIndex(s => s.id === action.elementId);
          if (currentIndex !== -1) {
            const section = updatedPage.layout.splice(currentIndex, 1)[0];
            updatedPage.layout.splice(action.position, 0, section);
          }
        } else if (action.elementType === 'row') {
          // Find the section containing this row
          for (const section of updatedPage.layout) {
            const currentIndex = section.rows.findIndex(r => r.id === action.elementId);
            if (currentIndex !== -1) {
              const row = section.rows.splice(currentIndex, 1)[0];
              section.rows.splice(action.position, 0, row);
              break;
            }
          }
        } else if (action.elementType === 'column') {
          // Find the row containing this column
          outerLoop: for (const section of updatedPage.layout) {
            for (const row of section.rows) {
              const currentIndex = row.columns.findIndex(c => c.id === action.elementId);
              if (currentIndex !== -1) {
                const column = row.columns.splice(currentIndex, 1)[0];
                row.columns.splice(action.position, 0, column);
                break outerLoop; // Exit both loops
              }
            }
          }
        }
        break;
      }
    }

    updatePage(updatedPage);
  }, [pageDoc, updatePage, readonly]);

  // Style update for selected element
  const handleStyleUpdate = useCallback((style: Style) => {
    if (!editorState.selectedElementId || !editorState.selectedElementType || editorState.selectedElementType === 'block') return;
    
    handleLayoutAction({
      type: 'UPDATE_STYLE',
      elementType: editorState.selectedElementType as 'view' | 'section' | 'row' | 'column',
      elementId: editorState.selectedElementId,
      style
    });
  }, [editorState.selectedElementId, editorState.selectedElementType, handleLayoutAction]);

  // Get style for selected element
  const getSelectedElementStyle = useCallback((): Style | undefined => {
    if (!editorState.selectedElementId || !editorState.selectedElementType) return undefined;

    if (editorState.selectedElementType === 'view') {
      return editorState.selectedElementId === pageDoc._id ? pageDoc.style : undefined;
    } else if (editorState.selectedElementType === 'section') {
      const section = pageDoc.layout.find(s => s.id === editorState.selectedElementId);
      return section?.style;
    } else if (editorState.selectedElementType === 'row') {
      for (const section of pageDoc.layout) {
        const row = section.rows.find(r => r.id === editorState.selectedElementId);
        if (row) return row.style;
      }
    } else if (editorState.selectedElementType === 'column') {
      for (const section of pageDoc.layout) {
        for (const row of section.rows) {
          const column = row.columns.find(c => c.id === editorState.selectedElementId);
          if (column) return column.style;
        }
      }
    }

    return undefined;
  }, [editorState.selectedElementId, editorState.selectedElementType, pageDoc]);

  // Helper function to convert Style to CSS properties
  const styleToCss = useCallback((style?: Style) => {
    if (!style) return {};

    const css: React.CSSProperties = {};

    // Handle padding
    if (style.padding) {
      if (typeof style.padding === 'number') {
        css.padding = `${style.padding}px`;
      } else {
        const { top, right, bottom, left } = style.padding;
        css.paddingTop = top ? `${top}px` : undefined;
        css.paddingRight = right ? `${right}px` : undefined;
        css.paddingBottom = bottom ? `${bottom}px` : undefined;
        css.paddingLeft = left ? `${left}px` : undefined;
      }
    }

    // Handle margin
    if (style.margin) {
      if (typeof style.margin === 'number') {
        css.margin = `${style.margin}px`;
      } else {
        const { top, right, bottom, left } = style.margin;
        css.marginTop = top ? `${top}px` : undefined;
        css.marginRight = right ? `${right}px` : undefined;
        css.marginBottom = bottom ? `${bottom}px` : undefined;
        css.marginLeft = left ? `${left}px` : undefined;
      }
    }

    // Background
    css.backgroundColor = style.backgroundColor || style.bg;
    css.backgroundImage = style.backgroundImage;
    css.backgroundSize = style.backgroundSize;
    css.backgroundPosition = style.backgroundPosition;
    css.backgroundRepeat = style.backgroundRepeat;

    // Border
    if (style.border) {
      if (style.border.width) {
        css.borderWidth = `${style.border.width}px`;
      }
      if (style.border.style) {
        css.borderStyle = style.border.style;
      }
      if (style.border.color) {
        css.borderColor = style.border.color;
      }
    }
    if (style.borderRadius) {
      css.borderRadius = `${style.borderRadius}px`;
    }

    // Text alignment
    css.textAlign = style.textAlign;

    // Layout & alignment
    css.justifyContent = style.justifyContent;
    css.alignItems = style.alignItems;

    // Dimensions
    css.minHeight = style.minHeight ? `${style.minHeight}px` : undefined;
    css.maxHeight = style.maxHeight ? `${style.maxHeight}px` : undefined;
    css.width = typeof style.width === 'number' ? `${style.width}px` : style.width;
    css.height = typeof style.height === 'number' ? `${style.height}px` : style.height;

    // Effects
    css.opacity = style.opacity;
    css.boxShadow = style.boxShadow;

    return css;
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {/* Left Panel Toggle Button (when collapsed) */}
        {!readonly && !showLeftPanel && (
          <Tooltip title="Show left panel (Ctrl/Cmd+B)" placement="right">
            <IconButton
              onClick={() => setShowLeftPanel(true)}
              sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                zIndex: 1100,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Left Panel - Components & Layout */}
        {!readonly && showLeftPanel && (
          <Paper sx={{
            width: 320,
            borderRadius: 0,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Left Panel Header with Collapse Button */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
              <Tabs
                value={leftPanelTab}
                onChange={(_, newValue) => setLeftPanelTab(newValue)}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Tab
                  label="Components"
                  value="components"
                  icon={<AppsIcon />}
                  iconPosition="start"
                  sx={{ minWidth: 130 }}
                />
                <Tab
                  label="Layout"
                  value="layout"
                  icon={<LayersIcon />}
                  iconPosition="start"
                  sx={{ minWidth: 130 }}
                />
              </Tabs>
              <Tooltip title="Hide left panel (Ctrl/Cmd+B)">
                <IconButton
                  size="small"
                  onClick={() => setShowLeftPanel(false)}
                  sx={{ mr: 1 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Left Panel Content */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: 0
            }}>
              {leftPanelTab === 'components' ? (
                <Box sx={{ 
                  flex: 1,
                  overflow: 'auto',
                  minHeight: 0,
                  height: '100%'
                }}>
                  <Box sx={{
                    p: 2,
                    pb: 10, // Increased padding at bottom to ensure last component is accessible
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1
                  }}>
                    {Object.entries(ComponentRegistry).map(([type, meta]) => (
                      <PaletteComponent key={type} type={type as ComponentType} meta={meta} />
                    ))}
                  </Box>
                </Box>
              ) : (
                <LayoutPanel
                  pageDoc={pageDoc}
                  selectedElementId={editorState.selectedElementId}
                  selectedElementType={editorState.selectedElementType}
                  onLayoutAction={handleLayoutAction}
                  onSelectElement={selectElement}
                  readonly={readonly}
                  expandedState={layoutExpandedState}
                  onExpandedStateChange={setLayoutExpandedState}
                />
              )}
            </Box>
          </Paper>
        )}

        {/* Main Canvas */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
          {/* Wrapper Box to constrain content to standard page width (1488px) for WYSIWYG accuracy */}
          {/* Using width: 100% to fill parent, maxWidth to constrain, and margin auto to center when wider than 1488px */}
          <Box sx={{ width: '100%', maxWidth: '1488px', py: 2, margin: '0 auto' }}>
            <Paper sx={{
              minHeight: '100%',
              borderRadius: 0,
              ...convertStyleToSx(pageDoc.style),
              // Apply outline when view is selected
              outline: (editorState.selectedElementId === pageDoc._id && editorState.selectedElementType === 'view') ? '2px solid' : 'none',
              outlineColor: (editorState.selectedElementId === pageDoc._id && editorState.selectedElementType === 'view') ? 'primary.main' : undefined,
              outlineOffset: (editorState.selectedElementId === pageDoc._id && editorState.selectedElementType === 'view') ? '2px' : undefined,
            }}>
            {/* Content area - don't show view metadata as page title */}

            {/* Render Sections */}
            {pageDoc.layout.map((section) => (
              <SectionRenderer
                key={section.id}
                section={section}
                selectedBlockId={editorState.selectedBlockId}
                selectedElementId={editorState.selectedElementId}
                selectedElementType={editorState.selectedElementType}
                onSelectBlock={selectBlock}
                onDeleteBlock={deleteBlock}
                onUpdateBlock={(updatedBlock) => {
                  const updatedPage = updateBlockInPage(pageDoc, updatedBlock);
                  updatePage(updatedPage);
                }}
                overContainer={overContainer}
                readonly={readonly}
                styleToCss={styleToCss}
              />
            ))}

            {/* Empty State */}
            {pageDoc.layout.length === 0 && (
              <EmptyPageDropZone />
            )}
            </Paper>
          </Box>
        </Box>

        {/* Right Panel Toggle Button (when collapsed) */}
        {!readonly && !showRightPanel && (editorState.selectedBlockId || editorState.selectedElementId) && (
          <Tooltip title="Show properties panel (Ctrl/Cmd+I)" placement="left">
            <IconButton
              onClick={() => setShowRightPanel(true)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                zIndex: 1100,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Right Panel - Configuration (Properties & Style) */}
        {!readonly && showRightPanel && (editorState.selectedBlockId || editorState.selectedElementId) && (
          <Paper sx={{
            width: 320,
            borderLeft: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: 0,
            position: 'relative'
          }}>
            {/* Panel Header with Collapse Button */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6">
                  {editorState.selectedBlockId ? 'Component Settings' : 'Layout Settings'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editorState.selectedBlockId
                    ? `Configure ${findBlockById(pageDoc, editorState.selectedBlockId)?.type || 'component'}`
                    : `Configure ${editorState.selectedElementType || 'element'}`
                  }
                </Typography>
              </Box>
              <Tooltip title="Hide properties panel (Ctrl/Cmd+I)">
                <IconButton
                  size="small"
                  onClick={() => setShowRightPanel(false)}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Panel Content */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {editorState.selectedBlockId ? (
                // Component Properties & Style
                (() => {
                  const selectedBlock = findBlockById(pageDoc, editorState.selectedBlockId);
                  if (!selectedBlock) return null;
                  
                  return (
                    <PropertyPanel
                      block={selectedBlock}
                      onUpdateBlock={(updatedBlock) => {
                        const updatedPage = updateBlockInPage(pageDoc, updatedBlock);
                        updatePage(updatedPage);
                      }}
                      onDeleteBlock={() => deleteBlock(editorState.selectedBlockId!)}
                      teamId={teamId}
                      organizationId={organizationId}
                    />
                  );
                })()
              ) : editorState.selectedElementId && editorState.selectedElementType && editorState.selectedElementType !== 'block' ? (
                // Layout Element Style
                <StylePanel
                  elementType={editorState.selectedElementType as 'section' | 'row' | 'column'}
                  elementId={editorState.selectedElementId}
                  style={getSelectedElementStyle()}
                  onUpdateStyle={handleStyleUpdate}
                  readonly={readonly}
                />
              ) : null}
            </Box>
          </Paper>
        )}
      </Box>
    </DndContext>
  );
};

// Palette Component (draggable)
const PaletteComponent: React.FC<{
  type: ComponentType;
  meta: any;
}> = ({ type, meta }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `component-${type}`,
    data: {
      type: 'component',
      componentType: type,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        p: 2,
        cursor: isDragging ? 'grabbing' : 'grab',
        border: 1,
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          component="span"
          sx={{ 
            fontFamily: 'Material Icons',
            fontSize: 20,
            color: 'text.secondary'
          }}
        >
          {meta.icon}
        </Box>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {meta.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta.description}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// Empty Page Drop Zone
const EmptyPageDropZone: React.FC = () => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'empty-page',
    data: {
      type: 'empty',
    },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        border: 2,
        borderStyle: 'dashed',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: isOver ? 'primary.light' : 'action.hover',
        opacity: isOver ? 0.8 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Start Building Your Page
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Drag components here to get started
      </Typography>
    </Box>
  );
};

// Section Renderer
interface SectionRendererProps {
  section: Section;
  selectedBlockId?: string;
  selectedElementId?: string;
  selectedElementType?: 'view' | 'section' | 'row' | 'column' | 'block';
  onSelectBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onUpdateBlock?: (updatedBlock: Block) => void;
  overContainer?: string | null;
  readonly?: boolean;
  styleToCss: (style?: Style) => React.CSSProperties;
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  selectedBlockId,
  selectedElementId,
  selectedElementType,
  onSelectBlock,
  onDeleteBlock,
  onUpdateBlock,
  overContainer,
  readonly = false,
  styleToCss
}) => {
  // Don't render if section is hidden
  if (section.visible === false) {
    return null;
  }

  const sectionSx = convertStyleToSx(section.style);
  const isSelected = selectedElementId === section.id && selectedElementType === 'section';

  return (
    <Box
      sx={{
        ...sectionSx,
        outline: isSelected ? '2px solid' : 'none',
        outlineColor: isSelected ? 'primary.main' : undefined,
        outlineOffset: isSelected ? '2px' : undefined,
      }}
    >
      {section.rows.map((row) => {
        // Don't render if row is hidden
        if (row.visible === false) {
          return null;
        }

        const rowSx = convertStyleToSx(row.style);
        const isRowSelected = selectedElementId === row.id && selectedElementType === 'row';

        return (
          <Box
            key={row.id}
            sx={{
              '&:last-child': { mb: 0 },
              ...rowSx,
              outline: isRowSelected ? '2px solid' : 'none',
              outlineColor: isRowSelected ? 'primary.main' : undefined,
              outlineOffset: isRowSelected ? '2px' : undefined,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              {row.columns.map((column) => {
                // Don't render if column is hidden
                if (column.visible === false) {
                  return null;
                }

                return (
                  <DropColumn
                    key={column.id}
                    column={column}
                    selectedBlockId={selectedBlockId}
                    selectedElementId={selectedElementId}
                    selectedElementType={selectedElementType}
                    onSelectBlock={onSelectBlock}
                    onDeleteBlock={onDeleteBlock}
                    onUpdateBlock={onUpdateBlock}
                    isOver={overContainer === column.id}
                    readonly={readonly}
                    styleToCss={styleToCss}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Drop Column Component
interface DropColumnProps {
  column: Column;
  selectedBlockId?: string;
  selectedElementId?: string;
  selectedElementType?: 'view' | 'section' | 'row' | 'column' | 'block';
  onSelectBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onUpdateBlock?: (updatedBlock: Block) => void;
  isOver?: boolean;
  readonly?: boolean;
  styleToCss: (style?: Style) => React.CSSProperties;
}

const DropColumn: React.FC<DropColumnProps> = ({
  column,
  selectedBlockId,
  selectedElementId,
  selectedElementType,
  onSelectBlock,
  onDeleteBlock,
  onUpdateBlock,
  isOver = false,
  readonly,
  styleToCss
}) => {
  const { setNodeRef } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      columnId: column.id,
    },
  });

  const columnSx = convertStyleToSx(column.style);
  const isSelected = selectedElementId === column.id && selectedElementType === 'column';

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: column.width || 1,
        borderColor: isOver ? 'primary.main' : undefined,
        bgcolor: isOver ? 'primary.light' : 'transparent',
        opacity: isOver ? 0.8 : 1,
        transition: 'all 0.2s ease',
        ...columnSx,
        outline: isSelected ? '2px solid' : 'none',
        outlineColor: isSelected ? 'primary.main' : undefined,
        outlineOffset: isSelected ? '2px' : undefined,
      }}
    >
      {column.blocks.map((block, index) => (
        <React.Fragment key={block.id}>
          {/* Drop zone before each block */}
          <DropZone
            columnId={column.id}
            index={index}
            isFirst={index === 0}
          />

          <DraggableBlock
            block={block}
            columnId={column.id}
            index={index}
            isSelected={block.id === selectedBlockId}
            onSelect={() => onSelectBlock(block.id)}
            onDelete={() => onDeleteBlock(block.id)}
            onUpdateBlock={onUpdateBlock}
            readonly={readonly}
          />
        </React.Fragment>
      ))}

      {/* Final drop zone after all blocks */}
      <DropZone
        columnId={column.id}
        index={column.blocks.length}
        isLast
      />

      {column.blocks.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 60,
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          Drop components here
        </Box>
      )}
    </Box>
  );
};

// Drop Zone between blocks
interface DropZoneProps {
  columnId: string;
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
}

const DropZone: React.FC<DropZoneProps> = ({ columnId, index, isFirst = false, isLast = false }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `dropzone-${columnId}-${index}`,
    data: {
      type: 'column',
      columnId: columnId,
      index: index,
    },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        height: isOver ? 20 : (isFirst || isLast ? 10 : 5),
        border: isOver ? 2 : 0,
        borderStyle: 'dashed',
        borderColor: 'primary.main',
        borderRadius: 1,
        bgcolor: isOver ? 'primary.light' : 'transparent',
        transition: 'all 0.2s ease',
        my: 0.5,
      }}
    />
  );
};

// Draggable Block Component
interface DraggableBlockProps {
  block: Block;
  columnId: string;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onUpdateBlock?: (updatedBlock: Block) => void;
  readonly?: boolean;
}

const DraggableBlock: React.FC<DraggableBlockProps> = ({
  block,
  columnId,
  index,
  isSelected,
  onSelect,
  onDelete,
  onUpdateBlock,
  readonly
}) => {
  const meta = ComponentRegistry[block.type];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: block.id,
    data: {
      type: 'block',
      block,
      sourceColumnId: columnId,
      sourceIndex: index,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  // Handle click with smarter propagation logic
  const handleBlockClick = (e: React.MouseEvent) => {
    // Don't select block if clicking on interactive content inside ViewContentQuery or ViewContentPick
    const target = e.target as HTMLElement;

    // Check if the click is on interactive elements
    const isClickableContent = target.closest('button') ||
                               target.closest('a') ||
                               target.closest('[class*="MuiCard-root"]') || // MUI Card
                               target.closest('[class*="MuiIconButton-root"]'); // MUI IconButton

    // If clicking on interactive content, don't select the block - let the event pass through
    if (!isClickableContent && onSelect) {
      onSelect();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={handleBlockClick}
      sx={{
        p: 0,  // Remove padding - let components handle their own spacing
        border: 2,
        borderColor: isSelected ? 'primary.main' : 'transparent',
        borderRadius: 1,
        bgcolor: 'transparent',  // Make wrapper transparent
        cursor: readonly ? 'default' : 'pointer',
        '&:hover': readonly ? {} : {
          borderColor: isSelected ? 'primary.main' : 'action.focus'
        },
        position: 'relative',
        mb: 1,
      }}
    >
      {/* Drag Handle */}
      {!readonly && (
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            cursor: isDragging ? 'grabbing' : 'grab',
            zIndex: 1000, // Ensure it appears above WYSIWYG editor
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Tooltip title="Drag to move">
            <IconButton size="small">
              <DragHandleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Delete Button */}
      {!readonly && isSelected && (
        <Box sx={{ 
          position: 'absolute', 
          top: 8, 
          right: 40,
          zIndex: 1000, // Ensure it appears above WYSIWYG editor
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
        }}>
          <Tooltip title="Delete block">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* WYSIWYG Component Renderer */}
      <ComponentRenderer
        block={block}
        isSelected={isSelected} 
        readonly={readonly || (block.type !== 'RichText' && block.type !== 'ViewContentPick' && block.type !== 'ViewContentQuery') || !isSelected} // Allow editing for selected RichText, ViewContentPick, and ViewContentQuery blocks
        onUpdate={(newProps: any) => {
          // Update the block's props directly
          if (onUpdateBlock) {
            const updatedBlock = { ...block, props: { ...block.props, ...newProps } };
            onUpdateBlock(updatedBlock);
          }
        }}
      />
    </Box>
  );
};

export default MinimalDragDropEditor;
